import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  TrendBadge,
} from '@agentselly/ui';

import { formatDate } from '~/helper/formatDate';
import { useTranslate } from '@tolgee/react';
import { useStatisticsRange } from '~/context/StatisticsRangeContext';
import { BarChart3, TrendingUp } from 'lucide-react';
import { Button } from '~/components/ui/Button';
import {
  filterWeeklyBuckets,
  weeklyBucketsToSeries,
  type WeeklyBucket,
} from '~/util/statisticsChart';

// Lazy load the LineChart component to reduce initial bundle size
const LineChart = lazy(() =>
  import('~/components/charts/LineChart').then((module) => ({
    default: module.LineChart,
  })),
);

interface Props {
  readonly title: string;
  readonly infoText?: string;
  readonly text: string;
  readonly linksTitle?: string;
  readonly links?: { label: string; url: string }[];
  readonly value: number;
  readonly changeInLastWeek?: number;
  readonly onlineSinceDate?: string;
  readonly lastChangeDate?: string;
  readonly lineChartData: WeeklyBucket[];
  readonly locale: string;
  readonly interestLink?: string;
}

export function CockpitStatisticsDetailWidget(props: Props) {
  const { t } = useTranslate();
  const { rangeKey } = useStatisticsRange();
  const [
    filteredData,
    setFilteredData,
  ] = useState<WeeklyBucket[]>(props.lineChartData);
  const [
    isChartVisible,
    setIsChartVisible,
  ] = useState(false);

  // The buckets are already aggregated per week — no per-day aggregation
  // needed. Map them directly to { x, y } points, chronologically sorted.
  const series = useMemo(
    () => weeklyBucketsToSeries(filteredData),
    [filteredData],
  );

  useEffect(() => {
    // Initialize chart visibility based on screen size after component mounts
    setIsChartVisible(window.innerWidth >= 768);

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsChartVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filterData = () => {
    try {
      if (!props.lineChartData || props.lineChartData.length === 0) {
        setFilteredData([]);
        return;
      }
      // Buckets are lückenlos pre-filled and chronologically ordered. Slice the
      // last N buckets by position in the array — no date arithmetic needed.
      setFilteredData(filterWeeklyBuckets(props.lineChartData, rangeKey));
    } catch (error) {
      console.error('Error filtering chart data:', error);
      setFilteredData([]);
    }
  };

  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rangeKey,
    props.lineChartData,
  ]);

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            {t(props.title)}
          </CardTitle>
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <BarChart3 className="h-4 w-4 text-primary" />
          </div>
        </div>
        {props.infoText && (
          <p className="text-sm text-muted-foreground">
            {t(props.infoText as any) || props.infoText}
          </p>
        )}
      </CardHeader>
      <CardContent className="flex-1 pt-0">
        <div
          className="flex flex-col md:flex-row h-full"
          style={{ minHeight: '400px' }}
        >
          <div className="flex flex-col justify-between w-full md:w-1/3 pr-4 mb-4 md:mb-0">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div
                  className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                    props.value
                      ? 'bg-primary-100 text-primary-600'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {props.value ? (
                    <TrendingUp className="h-6 w-6" />
                  ) : (
                    <BarChart3 className="h-6 w-6" />
                  )}
                </div>
                <div>
                  {props.value !== null && props.value !== undefined ? (
                    <div className="text-3xl font-bold tabular-nums text-gray-900 dark:text-gray-100">
                      {props.value}
                    </div>
                  ) : (
                    <div className="text-lg text-gray-500 dark:text-gray-400">
                      {t('keine_daten')}
                    </div>
                  )}
                  {props.changeInLastWeek !== null &&
                    props.changeInLastWeek !== undefined &&
                    props.changeInLastWeek !== 0 && (
                      <TrendBadge value={props.changeInLastWeek} />
                    )}
                </div>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-200 dark:text-gray-300">
                {t(props.text)}
              </p>
            </div>
            <div className="space-y-4">
              {props.links && props.links.length > 0 && (
                <div>
                  {props.linksTitle && (
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                      {t(props.linksTitle)}
                    </h4>
                  )}
                  <div className="space-y-1">
                    {props.links.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {t(link.label)}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                {props.onlineSinceDate && (
                  <div>
                    {t('online_seit')}:{' '}
                    {formatDate(new Date(props.onlineSinceDate))}
                  </div>
                )}
                {props.lastChangeDate && (
                  <div>
                    {t('zuletzt_aktualisiert')}:{' '}
                    {formatDate(new Date(props.lastChangeDate))}
                  </div>
                )}
              </div>

              {props.interestLink && (
                <div className="pt-2">
                  <a href={props.interestLink}>
                    <Button
                      variant="outline"
                      size="sm"
                      showArrow
                    >
                      {t('cockpit_statistics_to_interests')}
                    </Button>
                  </a>
                </div>
              )}
            </div>
          </div>
          <div
            className="w-full md:w-2/3 flex flex-col"
            style={{ minHeight: '350px' }}
          >
            {useMemo(
              () => (
                <div className="flex flex-col flex-1">
                  <button
                    onClick={() => setIsChartVisible(!isChartVisible)}
                    className="md:hidden mb-4 inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 dark:border-gray-700 bg-white dark:bg-gray-800 dark:bg-gray-900 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-300 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-gray-700 self-start"
                  >
                    {isChartVisible
                      ? t('cockpit_statistics_detail_widget_hide_chart')
                      : t('cockpit_statistics_detail_widget_show_chart')}
                  </button>

                  <div
                    className={`w-full relative ${
                      isChartVisible ? 'flex' : 'hidden md:flex'
                    } flex-1`}
                    style={{ minHeight: '300px' }}
                  >
                    <Suspense
                      fallback={
                        <div className="text-center py-10 w-full">
                          {t('cockpit_statistics_detail_widget_loading_chart')}
                        </div>
                      }
                    >
                      <div className="w-full h-full rounded-lg border border-slate-200 dark:border-slate-700 dark:border-gray-700 bg-white dark:bg-gray-800 dark:bg-gray-900 p-3">
                        <LineChart
                          datasetIdKey={
                            'cockpit_statistics_detail_widget_dataset'
                          }
                          series={series as any}
                          yFormat="number"
                        />
                      </div>
                    </Suspense>
                  </div>
                </div>
              ),
              [
                filteredData,
                isChartVisible,
                t,
              ],
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
